import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { api, type AdminUser } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FieldError } from "@/components/ui/field-error"
import { Button } from "@/components/ui/button"

export const Route = createFileRoute("/admin/_authed/profile")({
  component: ProfilePage,
})

function ProfilePage() {
  const queryClient = useQueryClient()
  const { data: me, isLoading } = useQuery<AdminUser | null>({
    queryKey: ["auth", "me"],
    queryFn: async () => (await api.get<AdminUser>("/auth/me")).data,
  })

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [currentPassword, setCurrentPassword] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!me) return
    setName(me.name)
    setEmail(me.email)
  }, [me])

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, string> = { name, email }
      if (password) {
        payload.current_password = currentPassword
        payload.password = password
        payload.password_confirmation = passwordConfirmation
      }
      const res = await api.put<AdminUser>("/auth/profile", payload)
      return res.data
    },
    onSuccess: (user) => {
      queryClient.setQueryData(["auth", "me"], user)
      setFieldErrors({})
      setCurrentPassword("")
      setPassword("")
      setPasswordConfirmation("")
      toast.success("Profile updated.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  if (isLoading) {
    return <div className="h-96 max-w-2xl animate-pulse rounded-2xl bg-secondary" />
  }

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl font-bold">Profile</h1>
      <p className="mt-1 text-sm text-muted-foreground">Update your account name, email and password.</p>

      <form
        className="mt-8 space-y-10"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-semibold">Account</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={!!fieldErrors.name}
              />
              <FieldError message={fieldErrors.name} />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={!!fieldErrors.email}
              />
              <FieldError message={fieldErrors.email} />
            </div>
          </div>
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <div>
            <h2 className="font-semibold">Change password</h2>
            <p className="mt-1 text-xs text-muted-foreground">Leave these blank to keep your current password.</p>
          </div>
          <div>
            <Label htmlFor="current_password">Current password</Label>
            <Input
              id="current_password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              aria-invalid={!!fieldErrors.current_password}
            />
            <FieldError message={fieldErrors.current_password} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={!!fieldErrors.password}
              />
              <FieldError message={fieldErrors.password} />
            </div>
            <div>
              <Label htmlFor="password_confirmation">Confirm new password</Label>
              <Input
                id="password_confirmation"
                type="password"
                autoComplete="new-password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
              />
            </div>
          </div>
        </section>

        <Button type="submit" disabled={save.isPending} className="rounded-full bg-gradient-accent">
          {save.isPending ? "Saving..." : "Save changes"}
        </Button>
      </form>
    </div>
  )
}
